import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Checkbox } from '@dreamtree-org/twreact-ui';
import { BatteryMedium, ShieldCheck, BadgeCheck } from 'lucide-react';
import { Money } from '@/components';
import { GRADE_VARIANT, batteryShown, conditionBadge, discountPct, gradeApplies, gradeLabel, specLine } from '../lib/listing';
import { useStorefront } from '../StorefrontContext';
import UnitImage from './UnitImage';
import { defaultCard } from '@dreamtree-org/landing-kit/schema';
import { cardPhotos, ratioClass } from '../lib/design';
import { cardSwatches, hasOptions, priceRange } from '../lib/variants';

// Secondary facts: always shown in a row card, from `sm` up in a stack card.
const WIDE = {
  row: { flex: 'flex', inline: 'inline', inlineFlex: 'inline-flex' },
  stack: { flex: 'hidden sm:flex', inline: 'hidden sm:inline', inlineFlex: 'hidden sm:inline-flex' },
};

/**
 * One exact unit in a grid (mockups 01/02). Shows the per-unit facts that differ
 * between refurbished units (C-02). The owner's card settings (D66) pick the
 * photo shape, badges, spec line, compare, quick add, MRP and hover effect.
 * `layout`: 'row' is horizontal on phones and vertical from `sm`; 'stack' is
 * always vertical (two cards per row on phones). In a stack, phones keep the
 * deciding facts (price, grade, battery, warranty, condition) and drop the seller
 * line and the % off badge, which return from `sm` (MERCH-57).
 */
// Interim composition until twreact-ui ships ProductCard: https://github.com/dreamtree-org/dreamtree-org.twreact-ui/issues/181
export default function ListingCard({ l, compare = true, layout = 'row' }) {
  const sf = useStorefront();
  const card = { ...defaultCard(), ...sf.card };
  const photos = cardPhotos(l);
  // A product model's catalog image stands in until the unit has a photo (MERCH-81, D95): always marked.
  const catalogCover = l.cover_kind === 'catalog' && photos.length > 0;
  const row = layout === 'row';
  const ratio = ratioClass(card.image_ratio, row);
  const zoom = card.hover === 'zoom' && photos.length ? 'transition-transform duration-300 group-hover:scale-105' : '';
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const off = discountPct(l);
  const href = `/p/${l.id}`;
  const inCart = sf.inCart(l.id);
  const checked = sf.compareIds.includes(l.id);
  // Stack cards sit two to a row on a 360px phone: secondary facts wait for `sm`.
  // Whole class names, so Tailwind's scanner sees them.
  const wide = row ? WIDE.row : WIDE.stack;

  // A product with options (MERCH-87) is chosen on its page: the card shows its price range and colours.
  const choosing = hasOptions(l);
  const range = priceRange(l);
  const swatches = cardSwatches(l);

  const onAdd = async () => {
    if (choosing) return navigate(href);
    if (inCart) return navigate('/cart');
    setBusy(true);
    const r = await sf.addToCart(l);
    setBusy(false);
    setNote(r.ok ? 'Added to cart' : r.error);
  };

  const onCompare = () => setNote(sf.toggleCompare(l.id) || '');

  return (
    <Card hover className={`group flex h-full min-w-0 sm:gap-3 sm:p-4 ${row ? 'flex-row gap-3 p-3 sm:flex-col' : 'flex-col gap-2 p-2.5'}`}>
      <Link to={href} className={`relative block shrink-0 overflow-hidden rounded-lg ${row ? 'w-28 sm:w-auto' : ''}`} aria-label={`View ${l.title}`}>
        <UnitImage src={photos[0]} className={`${ratio} ${zoom}`} size="md" pending label={catalogCover ? `${l.title}, ${String(l.cover_label || 'catalog image, not this unit').toLowerCase()}` : `${l.title}, front view`} icon={(sf.shop.typeOf(l) || {}).icon} />
        {catalogCover ? (
          <span className="absolute bottom-2 left-2" data-testid="card-catalog-badge" aria-hidden="true">
            <Badge size="xs" variant="warning">Catalog image</Badge>
          </span>
        ) : null}
        {card.hover === 'second_image' && photos[1] && !catalogCover ? (
          <img src={photos[1]} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full rounded-lg object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        ) : null}
        {card.badges ? (
          <span className="absolute left-2 top-2 flex flex-col items-start gap-1">
            <span data-testid="card-condition"><Badge size="xs" variant="success">{conditionBadge(l)}</Badge></span>
            {off ? <span className={wide.inlineFlex} data-testid="card-off-badge"><Badge size="xs" variant="error">{off}% off</Badge></span> : null}
          </span>
        ) : null}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide opacity-60">{l.brand}</div>
          <Link to={href} className="block break-words font-semibold leading-snug hover:underline">
            {[l.series, l.model_name].filter(Boolean).join(' ') || l.title}
          </Link>
          {card.spec_line ? <div className="mt-0.5 line-clamp-2 break-words text-xs opacity-70 sm:text-sm" data-testid="card-spec">{specLine(l, sf.shop.typeOf(l))}</div> : null}
        </div>

        <div className="flex flex-wrap gap-1.5 text-xs">
          {/* Owner turned photo badges off: the condition still shows (D98, never an item without its label). */}
          {!card.badges ? <span data-testid="card-condition"><Badge size="xs" variant="success">{conditionBadge(l)}</Badge></span> : null}
          {gradeApplies(l) ? <Badge size="xs" variant={GRADE_VARIANT[l.grade] || 'default'}>{gradeLabel(l.grade)}</Badge> : null}
          {batteryShown(l) ? (
            <Badge size="xs" variant="outline"><span className="inline-flex items-center gap-1"><BatteryMedium className="h-3 w-3" aria-hidden="true" />{l.battery_health_pct}%</span></Badge>
          ) : null}
          {l.warranty_months ? (
            <Badge size="xs" variant="outline"><span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" aria-hidden="true" />{l.warranty_months} mo</span></Badge>
          ) : null}
        </div>

        {swatches.values.length ? (
          <div className="flex items-center gap-1" aria-label={`${swatches.name}: ${swatches.values.map((v) => v.value).join(', ')}`} data-testid="card-swatches">
            {/* Product colours the store set (#rrggbb, validated on save): data, not theme. */}
            {swatches.values.map((v) => (
              <span key={v.value} title={v.value} aria-hidden="true" className="inline-block h-3.5 w-3.5 rounded-full border border-[var(--app-border)]" style={v.swatch ? { backgroundColor: v.swatch } : undefined} />
            ))}
            {swatches.more ? <span className="text-xs opacity-70">+{swatches.more}</span> : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-baseline gap-x-2">
          {range.varies ? (
            <span className="text-lg font-bold sm:text-xl" data-testid="card-price-range"><Money paise={range.min} /> – <Money paise={range.max} /></span>
          ) : <Money paise={l.price_paise} className="text-lg font-bold sm:text-xl" />}
          {card.mrp && !range.varies && l.mrp_paise > l.price_paise ? <span className="text-xs line-through opacity-50">MRP <Money paise={l.mrp_paise} /></span> : null}
          {off && !card.badges ? <span className={`${wide.inline} text-xs font-semibold text-success-700`} data-testid="card-off-text">{off}% off</span> : null}
        </div>

        <div className={`${wide.flex} min-w-0 items-center gap-1 text-xs opacity-80`} data-testid="card-seller">
          <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary-600" aria-hidden="true" />
          <span className="truncate">{l.sold_by ? `Sold by ${l.sold_by} · Fulfilled via ${sf.seller.name}` : `${sf.seller.name} · Verified seller`}</span>
        </div>

        {(compare && card.compare) || card.quick_add ? (
          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
            {compare && card.compare ? <Checkbox size="sm" label="Compare" checked={checked} onChange={onCompare} ariaLabel={`Compare ${l.title}`} /> : <span />}
            {card.quick_add ? (
              <Button size="sm" variant={inCart && !choosing ? 'outline' : 'primary'} loading={busy} onClick={onAdd}>
                {choosing ? 'Choose options' : inCart ? 'In cart' : 'Add to cart'}
              </Button>
            ) : null}
          </div>
        ) : null}
        {note ? <div className="text-xs opacity-70" role="status">{note}</div> : null}
      </div>
    </Card>
  );
}
